import type { AxiosError } from 'axios';
import { auth0 } from '@/plugins/auth0';
import router from '@/router/router';
import type { IWorkSpace } from '@/types/appKeysTypes';

export const clearSessionStore = () => {
  Object.keys(sessionStorage)
    .filter((key) => key.toLowerCase().includes('_store'))
    .forEach((key) => sessionStorage.removeItem(key));
};

// Explicit Log out — no target to preserve, so do a full Auth0 logout back to the app origin.
export const signOut = () => {
  clearSessionStore();
  return auth0.logout({ logoutParams: { returnTo: window.location.origin } });
};

// Auth failure — re-log while preserving the deep-link target, keeping the Auth0 session.
export const reauthenticate = () => {
  clearSessionStore();
  return router.push(buildLoginRoute(window.location.pathname, window.location.search));
};

// Auth0 authenticated the user but will not issue a token for this app; re-logging in
// cannot fix that, so the session is left intact and the user is sent somewhere terminal.
export const showAccessDenied = () => router.push({ name: 'AccessDenied' });

export const buildLoginRoute = (pathname: string, search: string) => ({
  name: 'Login' as const,
  query: { target: encodeURI(pathname + search) },
});

export const isInvalidTokenChallenge = (error: unknown): boolean => {
  const challenge = (error as AxiosError)?.response?.headers?.['www-authenticate'];
  if (typeof challenge === 'string' && challenge.includes('error="invalid_token"')) {
    // API has rejected bearer token
    return true;
  } else {
    // Ordinary 401
    return false;
  }
};

// A persisted workspace is only usable if it still matches one the user is allowed into;
// otherwise it is stale (access revoked, reassigned) and must be discarded.
export const resolveWorkspace = (
  persisted: IWorkSpace | '' | null | undefined,
  allowed: IWorkSpace[]
): IWorkSpace | undefined => {
  if (!persisted || !persisted.appKey) return undefined;
  return allowed.find((workspace) => workspace.appKey === persisted.appKey);
};
